import React, { useEffect } from 'react';
import styled from 'styled-components';
import { colors, Label, ArrowIcon, ChevronIcon, EllipsisIcon } from '@commonsku/styles';
import { skuFont } from '../../helpers/styles';
import { HierarchicalMenuFilterProps, HierarchicalMenuItem } from './types';

const CategoryOptionRow = styled.div<{ $isRefined?: boolean }>`
  background-color: ${({ $isRefined }) => $isRefined ? 'white' : 'transparent'};
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-left: 4px;
  padding-right: 4px;
  height: 32px;
  align-items: center;
`;

const CategoryLabel = styled(Label)<{ $isRefined?: boolean }>`
  text-overflow: ellipsis;
  font-family: ${skuFont};
  overflow: hidden;
  white-space: nowrap;
  color: ${({ $isRefined }) => $isRefined ? colors.neutrals[100] : colors.primary1[65]};
  font-weight: ${({ $isRefined }) => $isRefined ? 600 : 400};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HeaderLabel = styled(Label)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const HierarchicalMenuFilter: React.FC<HierarchicalMenuFilterProps> = ({
  label,
  items,
  onRefine,
  canToggleShowMore,
  isShowingMore,
  onToggleShowMore,
  limit,
  onShowMore,
}) => {
  let parentItem: HierarchicalMenuItem | null = null;
  let parentItems: HierarchicalMenuItem[] = [];

  useEffect(() => {
    if (isShowingMore && onShowMore) {
      onShowMore(true);
    }
  }, [isShowingMore, onShowMore]);

  const findSelectedItem = (menuItems: HierarchicalMenuItem[]): HierarchicalMenuItem | null => {
    parentItems = menuItems;
    for (const item of menuItems) {
      if (item.isRefined) {
        if (item.data && item.data.length > 0) {
          parentItem = item;
          const refinedChild = findSelectedItem(item.data);
          if (refinedChild) {
            return refinedChild;
          }
        }
        return item;
      }
    }
    return null;
  };

  const selectedItem = findSelectedItem(items);

  const getItems = (): HierarchicalMenuItem[] => {
    if (selectedItem && selectedItem.data && selectedItem.data.length > 0) {
      return selectedItem.data;
    }
    return parentItems;
  };

  const itemData = getItems();

  const getLabel = (): string => {
    if (selectedItem && selectedItem.data && selectedItem.data.length > 0) {
      return selectedItem.label;
    }
    if (parentItem && parentItem.label) {
      return parentItem.label;
    }
    return label;
  };

  return (
    <div>
      <HeaderRow>
        {parentItem && <ArrowIcon direction="left" onClick={() => onRefine(parentItem!.value)} />}
        <HeaderLabel>
          {getLabel()}
        </HeaderLabel>
      </HeaderRow>
      <div>
        {itemData.map((option) => (
          <CategoryOptionRow
            key={option.value}
            $isRefined={option.isRefined}
            onClick={() => onRefine(option.value)}
          >
            <CategoryLabel
              $isRefined={option.isRefined}
            >
              {option.label}
            </CategoryLabel>
            {!option.isRefined && <ChevronIcon />}
          </CategoryOptionRow>
        ))}
        {canToggleShowMore && !isShowingMore && itemData.length >= limit && (
          <CategoryOptionRow
            onClick={onToggleShowMore}
          >
            <CategoryLabel>
              More Categories
            </CategoryLabel>
            <EllipsisIcon />
          </CategoryOptionRow>
        )}
      </div>
    </div>
  );
};

export default HierarchicalMenuFilter;
